import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "clearhealthThemeMode";
const VALID_MODES = ["system", "light", "dark"];

const ThemeContext = createContext({
  mode: "system",
  resolvedTheme: "light",
  isDark: false,
  setMode: () => {},
  cycleMode: () => {},
});

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const getStoredMode = () => {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_MODES.includes(stored) ? stored : "system";
  } catch {
    return "system";
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(getStoredMode);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  const resolvedTheme = mode === "system" ? systemTheme : mode;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => setSystemTheme(event.matches ? "dark" : "light");

    handleChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    root.classList.toggle("dark", resolvedTheme === "dark");
    root.dataset.theme = resolvedTheme;
    root.dataset.themeMode = mode;
    root.style.colorScheme = resolvedTheme;

    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore storage failures; the current session still receives the theme.
    }
  }, [mode, resolvedTheme]);

  const setMode = useCallback((nextMode) => {
    setModeState(VALID_MODES.includes(nextMode) ? nextMode : "system");
  }, []);

  const cycleMode = useCallback(() => {
    const index = VALID_MODES.indexOf(mode);
    setMode(VALID_MODES[(index + 1) % VALID_MODES.length]);
  }, [mode, setMode]);

  const value = useMemo(
    () => ({
      mode,
      resolvedTheme,
      isDark: resolvedTheme === "dark",
      setMode,
      cycleMode,
    }),
    [cycleMode, mode, resolvedTheme, setMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
